import React, { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Save, BookmarkPlus, Bookmark, Trash2, Star, ChevronDown } from 'lucide-react';

const SavedFilters = ({ currentFilters, onLoadFilter, onFiltersChange }) => {
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const fetchSavedFilters = async () => {
    try {
      const { data } = await api.get('/filter-preferences');
      setSavedFilters(data);
      
      // Auto-load default filter
      const defaultFilter = data.find(f => f.is_default);
      if (defaultFilter && onLoadFilter) {
        onLoadFilter(defaultFilter.filters);
      }
    } catch (err) {
      console.error('Failed to fetch saved filters:', err);
    }
  };

  useEffect(() => {
    fetchSavedFilters();
  }, []);

  const handleSave = async () => {
    if (!filterName.trim()) {
      toast.error('Inserisci un nome per il filtro');
      return;
    }

    try {
      await api.post('/filter-preferences', {
        name: filterName,
        filters: currentFilters,
        is_default: isDefault
      });
      toast.success('Filtro salvato');
      setSaveDialogOpen(false);
      setFilterName('');
      setIsDefault(false);
      fetchSavedFilters();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleLoad = (filters) => {
    if (onLoadFilter) {
      onLoadFilter(filters);
    }
    toast.success('Filtro applicato');
  };

  const handleSetDefault = async (filterId, filters) => {
    try {
      await api.put(`/filter-preferences/${filterId}`, {
        ...filters,
        is_default: true
      });
      toast.success('Filtro impostato come predefinito');
      fetchSavedFilters();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (filterId) => {
    try {
      await api.delete(`/filter-preferences/${filterId}`);
      toast.success('Filtro eliminato');
      fetchSavedFilters();
    } catch (err) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Save current filter */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" data-testid="save-filter-button">
            <BookmarkPlus className="w-4 h-4" />
            Salva Filtro
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Salva Filtro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nome Filtro</Label>
              <Input
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="es. Ticket urgenti Milano"
                data-testid="filter-name-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Imposta come filtro predefinito
              </Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSave} className="bg-blue-900 hover:bg-blue-800">
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load saved filter */}
      {savedFilters.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="load-filter-button">
              <Bookmark className="w-4 h-4" />
              Filtri Salvati
              <ChevronDown className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>I Miei Filtri</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {savedFilters.map(filter => (
              <DropdownMenuItem
                key={filter.id}
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handleLoad(filter.filters)}
              >
                <span className="flex items-center gap-2">
                  {filter.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                  {filter.name}
                </span>
                <div className="flex items-center gap-1">
                  {!filter.is_default && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(filter.id, filter);
                      }}
                    >
                      <Star className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-500 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(filter.id);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default SavedFilters;
